// ============================================
// Accordion.js
// GSAP を使ったアコーディオン（開閉アニメーション）
//
// 使用ライブラリ:
//   GSAP : https://gsap.com/
//
// 機能:
//   - <details> / <summary> の標準 HTML をデフォルト構成として JS でアニメーション制御する
//   - summary の標準開閉（即時切り替え）を preventDefault で止め、GSAP の開閉に置き換える
//   - div 構成で使った場合も role / tabindex / aria-expanded を自動補完して動作する
//   - アニメーション中のクリック連打を防止する（is-running フラグ管理）
//   - 複数アコーディオンが独立して動作する
//
// ============================================
// HTML 構造例（デフォルト: details / summary）:
// ============================================
//
// <!-- アコーディオン1つ分 -->
// <details class="js-accordion">
//
//   <!-- クリックするタイトル部分（summary はネイティブでキーボード操作可能） -->
//   <summary class="js-accordion__ttlWrap">
//     <h3 class="c-accordion__ttl">よくある質問のタイトル</h3>
//     <!-- アイコンなどをここに入れてもよい -->
//   </summary>
//
//   <!-- 開閉するコンテンツ部分（初期状態は CSS で height:0; overflow:hidden にする） -->
//   <div class="js-accordion__contents">
//     <div class="c-accordion__body">
//       <p>ここに回答や詳細テキストを入れる。</p>
//     </div>
//   </div>
//
// </details>
//
// <!-- 複数並べる場合はそのまま繰り返す -->
// <details class="js-accordion">...</details>
// <details class="js-accordion">...</details>
//
// ※ details / summary を使えない場合は div でも動作する。
//    その場合は JS が role="button" / tabindex="0" / aria-expanded を自動付与する。
//
//   <div class="js-accordion">
//     <div class="js-accordion__ttlWrap">...</div>
//     <div class="js-accordion__contents">...</div>
//   </div>
//
// CSS の注意点:
//   .js-accordion__contents {
//     overflow: hidden;  /* 必須: height アニメーションを機能させる */
//     height: 0;         /* 初期状態は閉じておく */
//   }
//   summary はテーマの foundation/_base.scss でマーカー（▶）非表示済み。
// ============================================

import { gsap } from 'gsap';
import { bindOnce } from '../utils/bindOnce';

// ============================================
// 設定値
// ここを変更するだけでアニメーションの見た目を調整できる
// ============================================
const CONFIG = {
  // タイトルとコンテンツの間の余白（px）
  // 開いたとき content の上に付く marginBottom の値
  contentMarginBottom: 20,

  // アコーディオンを閉じるときのアニメーション時間（秒）
  closeDuration: 0.8,

  // アコーディオンを開くときのアニメーション時間（秒）
  openDuration: 0.8,

  // イージング（https://gsap.com/docs/v3/Eases/ で確認できる）
  closeEase: 'power1.inOut',
  openEase: 'power2.inOut',

  // 開いた状態を示す属性（<details> の open 属性に相当）
  openAttr: 'open',

  // アニメーション中であることを示す data 属性値
  runningValue: 'running',
};

// ============================================
// accordionFunc — ページ内の全アコーディオンを初期化する
// ============================================
function accordionFunc() {
  const triggers = document.querySelectorAll('.js-accordion__ttlWrap');
  if (triggers.length === 0) return;

  triggers.forEach((trigger) => {
    // バインド済みチェック（DOMContentLoaded 後に追加された要素の二重登録防止）
    if (!bindOnce(trigger, 'accordion')) return;

    const accordion = trigger.closest('.js-accordion');
    const content = accordion?.querySelector('.js-accordion__contents');

    // 必要な要素が揃っていなければ初期化しない
    if (!accordion || !content) return;

    // <summary> / <button> はネイティブでフォーカス・キーボード操作に対応しているため、
    // div などの非インタラクティブ要素が使われた場合のみ JS で属性を補完する
    const isNativeInteractive = trigger.tagName === 'SUMMARY' || trigger.tagName === 'BUTTON';

    if (!isNativeInteractive) {
      if (!trigger.hasAttribute('role')) {
        trigger.setAttribute('role', 'button');
      }
      if (!trigger.hasAttribute('tabindex')) {
        trigger.setAttribute('tabindex', '0');
      }
    }

    // 開閉状態の伝達:
    // <summary> は親 <details> の open 属性でネイティブに伝わるため aria-expanded を付けない
    // （二重に付けると支援技術の読み上げが重複・矛盾する可能性がある）
    const usesAriaExpanded = trigger.tagName !== 'SUMMARY';
    const syncExpanded = (isOpen) => {
      if (usesAriaExpanded) {
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      }
    };
    syncExpanded(accordion.hasAttribute(CONFIG.openAttr));

    const toggle = () => {
      // アニメーション中の場合はスキップ（クリック連打防止）
      if (accordion.dataset.animStatus === CONFIG.runningValue) return;

      // open 属性の有無で開閉を判定する（<details> の open と同じ考え方）
      if (accordion.hasAttribute(CONFIG.openAttr)) {
        syncExpanded(false);
        closeAccordion(accordion, content);
      } else {
        syncExpanded(true);
        openAccordion(accordion, content);
      }
    };

    trigger.addEventListener('click', (e) => {
      // <summary> の場合: ブラウザ標準の即時開閉を止めて GSAP アニメーションに置き換える
      // <a> の場合: ページ遷移を止める
      e.preventDefault();
      toggle();
    });

    // Enter / Space キーでも開閉できるようにする（role="button" の標準挙動に合わせる）。
    // summary / button はキー押下でネイティブに click イベントが発火するため、
    // ここで登録すると二重発火になる。div 構成の場合のみ登録する。
    if (!isNativeInteractive) {
      trigger.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault(); // Space キーでのページスクロールを防ぐ
        toggle();
      });
    }

    // 閉じるボタン
    const closeBtn = accordion.querySelector('.js-accordion__cls');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (accordion.hasAttribute(CONFIG.openAttr) && accordion.dataset.animStatus !== CONFIG.runningValue) {
          syncExpanded(false);
          closeAccordion(accordion, content);
        }
      });
    }
  });
}

// アコーディオンを閉じるアニメーション
function closeAccordion(accordion, content) {
  accordion.dataset.animStatus = CONFIG.runningValue; // アニメーション開始フラグ

  gsap.fromTo(
    content,
    {
      // 閉じる前の現在の高さ・透明度・マージンを取得して開始値にする
      height: content.offsetHeight,
      opacity: 1,
      marginBottom: CONFIG.contentMarginBottom,
    },
    {
      height: 0,
      opacity: 0,
      marginBottom: 0,
      duration: CONFIG.closeDuration,
      ease: CONFIG.closeEase,
      onComplete: () => {
        accordion.removeAttribute(CONFIG.openAttr); // 閉じた状態を示す属性を外す
        gsap.set(content, { clearProps: 'height' }); // GSAP のインラインスタイルを掃除する
        accordion.dataset.animStatus = ''; // アニメーション完了フラグをリセット
      },
    }
  );
}

// アコーディオンを開くアニメーション
function openAccordion(accordion, content) {
  accordion.setAttribute(CONFIG.openAttr, ''); // 先に open 属性を付ける（DOM に表示させる）
  accordion.dataset.animStatus = CONFIG.runningValue;

  // open にした直後は height が auto になるため、
  // scrollHeight で実際の高さを取得してからアニメーション開始する
  // requestAnimationFrame で 1 フレーム待つことで scrollHeight を正確に取れる
  requestAnimationFrame(() => {
    const targetH = content.scrollHeight;

    gsap.fromTo(
      content,
      {
        height: 0,
        opacity: 0,
        marginTop: 0,
      },
      {
        height: targetH,
        opacity: 1,
        marginBottom: CONFIG.contentMarginBottom,
        duration: CONFIG.openDuration,
        ease: CONFIG.openEase,
        onComplete: () => {
          // アニメーション完了後は height を auto に戻す
          // （コンテンツが動的に変化しても追従できるようにする）
          gsap.set(content, { height: 'auto' });
          accordion.dataset.animStatus = '';
        },
      }
    );
  });
}

export { accordionFunc };
